import universalRoutes from '@/routes/universal.json'
import centralRoutes from '@/routes/central.json'
import tenantRoutes from '@/routes/tenant.json'
import coreRoutes from '@/routes/core.json'

const fallbackRoutes = {
    ...universalRoutes,
    ...centralRoutes,
    ...tenantRoutes,
    ...coreRoutes,
}

const moduleRouteFiles = import.meta.glob(
    '../../../Modules/*/Resources/js/routes.json',
    { eager: true }
) as Record<string, { default: any }>

const normalizeText = (value?: string | null) =>
    (value ?? '').toString().trim().toLowerCase()

const normalizeAlias = (value?: string | null) =>
    normalizeText(value).replaceAll(/[^a-z0-9]/g, '')

const extractModuleFolderFromPath = (path: string) => {
    const parts = path.split('/Modules/')
    if (parts.length < 2) {
        return ''
    }

    return parts[1].split('/Resources/js/routes.json')[0]
}

const resolveModuleRouteFile = (moduleName: string) => {
    const normalizedModuleName = normalizeText(moduleName)
    const normalizedModuleAlias = normalizeAlias(moduleName)

    const exactMatch = Object.keys(moduleRouteFiles).find(path => {
        const folderName = extractModuleFolderFromPath(path)
        return normalizeText(folderName) === normalizedModuleName
    })

    if (exactMatch) {
        return exactMatch
    }

    return Object.keys(moduleRouteFiles).find(path => {
        const folderName = extractModuleFolderFromPath(path)
        return normalizeAlias(folderName) === normalizedModuleAlias
    })
}

export function buildRoutes(context: string = null, activeModules: string[] = []) {
    const normalizedContext = normalizeText(context)
    const isCentralContext = normalizedContext === 'central'
    const isTenantContext = normalizedContext === 'tenant' || !isCentralContext

    let routes = { ...universalRoutes }

    // central routes
    if (isCentralContext) {
        routes = { ...routes, ...centralRoutes, ...coreRoutes }
    } else {
        routes = { ...routes, ...tenantRoutes, ...coreRoutes }
    }

    // tenant modules
    if (isTenantContext) {
        activeModules.forEach(module => {
            const file = resolveModuleRouteFile(module)

            if (!file) {
                console.warn(`Routes not found for module: ${module}`)
                return
            }
            routes = { ...routes, ...moduleRouteFiles[file].default }
        })
    }
    return routes
}

export function createRouteHelper(routes: any) {
    return function route(
        name: string,
        params: Record<string, any> | any = {},
        extraQuery: Record<string, any> = {}
    ) {
        const routeDefinition = routes[name] ?? fallbackRoutes[name]
        let uri = routeDefinition?.uri

        if (!uri) {
            throw new Error(`Route ${name} not found`)
        }

        const placeholders = [...uri.matchAll(/\{([^}]+)\}/g)]
        const placeholderKeys = placeholders.map(m => m[1].replace(/\?$/, ''))

        let normalizedParams: Record<string, any> = {}

        // ------------------------------------------------
        // 1️⃣ normalize params (Ziggy behavior)
        // ------------------------------------------------
        if (params == null) {
            normalizedParams = {}
        }

        // route('users.show', 5)
        else if (typeof params === 'string' || typeof params === 'number') {
            if (placeholderKeys.length === 1) {
                normalizedParams[placeholderKeys[0]] = params
            } else {
                normalizedParams.id = params
            }
        }

        // route('users.show', [1,2])
        else if (Array.isArray(params)) {
            normalizedParams = placeholderKeys.reduce((acc, key, index) => {
                acc[key] = params[index]
                return acc
            }, {} as Record<string, any>)
        }

        // route('users.show', {id: 1})
        else if (typeof params === 'object') {
            normalizedParams = { ...params }
        }

        // ------------------------------------------------
        // 2️⃣ fill route params (SMART MATCH)
        // ------------------------------------------------
        const usedParamKeys = new Set<string>()

        placeholders.forEach(match => {
            const rawKey = match[1]
            const isOptional = rawKey.endsWith('?')
            const key = rawKey.replace(/\?$/, '')
            const token = match[0]

            let value = normalizedParams[key]

            // 🔥 Ziggy magic: {user} ← id
            if (value === undefined && normalizedParams.id !== undefined) {
                value = normalizedParams.id
                usedParamKeys.add('id')
            }

            if (value !== undefined && value !== null && value !== '') {
                usedParamKeys.add(key)
                uri = uri.replace(token, encodeURIComponent(String(value)))
                return
            }

            if (isOptional) {
                uri = uri.replace(`/${token}`, '').replace(token, '')
                return
            }

            throw new Error(`Missing required route parameter: ${key}`)
        })

        // ------------------------------------------------
        // 3️⃣ leftover → query string
        // ------------------------------------------------
        const queryParams = new URLSearchParams()

        const finalQuery = {
            ...normalizedParams,
            ...extraQuery,
        }

        Object.keys(finalQuery).forEach(key => {
            if (usedParamKeys.has(key)) return

            const value = finalQuery[key]
            if (value === undefined || value === null || value === '') return

            if (Array.isArray(value)) {
                value.forEach(v => queryParams.append(`${key}[]`, String(v)))
            } else if (typeof value === 'object') {
                Object.keys(value).forEach(subKey => {
                    const subVal = value[subKey]
                    if (subVal === undefined || subVal === null || subVal === '') return

                    if (Array.isArray(subVal)) {
                        subVal.forEach(v => queryParams.append(`${key}[${subKey}][]`, String(v)))
                    } else {
                        queryParams.append(`${key}[${subKey}]`, String(subVal))
                    }
                })
                return
            } else {
                queryParams.append(key, String(value))
            }
        })

        // ------------------------------------------------
        // 4️⃣ cleanup url
        // ------------------------------------------------
        uri = uri.replace(/\/+/g, '/')
        uri = uri.replace(/\/$/, '')

        const path = `/${uri.replace(/^\/+/, '')}`
        const queryString = queryParams.toString()

        return `${window.location.origin}${path}${queryString ? `?${queryString}` : ''}`
    }
}

export const getRoutes = (routes: any) => {
    return routes; 
}